import { Head, Link } from '@inertiajs/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useSidebarState } from '@/components/CollapsibleSidebar';

/**
 * Guide data for navigation.
 */
interface GuideNav {
    id: number;
    title: string;
    slug: string;
    icon: string;
    is_published: boolean;
    is_current: boolean;
}

/**
 * Current guide with content.
 */
interface CurrentGuide {
    id: number;
    title: string;
    slug: string;
    description: string | null;
    content: string;
    icon: string;
    is_published: boolean;
    updated_at: string | null;
}

/**
 * Props for the University Show page.
 */
interface Props {
    guide: CurrentGuide;
    guides: GuideNav[];
    prevGuide: GuideNav | null;
    nextGuide: GuideNav | null;
    isGm: boolean;
}

/**
 * University Show - Displays a single guide with navigation.
 * Styled as a study guide with parchment styling.
 */
export default function Show({ guide, guides, prevGuide, nextGuide, isGm }: Props) {
    const { isOpen: sidebarOpen, isMobile, toggle: toggleSidebar, close: closeSidebar } = useSidebarState(true, false);

    return (
        <>
            <Head title={`${guide.title} - Sellune University`} />

            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex relative">
                {/* Mobile overlay */}
                {isMobile && sidebarOpen && (
                    <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={closeSidebar} />
                )}

                {/* Sidebar - Guide List */}
                <aside className={`${sidebarOpen ? 'w-72' : 'w-0'} transition-all duration-300 bg-slate-800/80 border-r border-blue-900/30 overflow-hidden flex-shrink-0 ${isMobile ? 'fixed inset-y-0 left-0 z-50' : 'relative'}`}>
                    <div className="w-72 h-full flex flex-col">
                        {/* Header */}
                        <div className="p-4 border-b border-blue-900/30 bg-blue-900/20">
                            <Link href="/university" className="flex items-center gap-2 text-blue-200 hover:text-blue-100 transition-colors">
                                <span className="text-2xl">🎓</span>
                                <span className="font-serif font-medium">Sellune University</span>
                            </Link>
                        </div>

                        {/* Guide List */}
                        <nav className="flex-1 overflow-y-auto p-3">
                            <ul className="space-y-1">
                                {guides.map((g) => (
                                    <li key={g.id}>
                                        <Link
                                            href={`/university/${g.slug}`}
                                            onClick={() => isMobile && closeSidebar()}
                                            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                                                g.is_current
                                                    ? 'bg-blue-800/40 text-blue-100 border-l-2 border-blue-400'
                                                    : 'text-blue-200/80 hover:bg-blue-800/20 hover:text-blue-100'
                                            }`}
                                        >
                                            <span className="text-lg flex-shrink-0">{g.icon}</span>
                                            <span className="flex-1 truncate">{g.title}</span>
                                            {isGm && !g.is_published && (
                                                <span className="text-xs text-yellow-400">(Draft)</span>
                                            )}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    </div>
                </aside>

                {/* Toggle Button */}
                <button
                    onClick={toggleSidebar}
                    className={`fixed top-4 z-50 p-2.5 bg-slate-800/90 border border-blue-900/30 rounded-lg text-blue-200 hover:bg-blue-800/30 transition-all duration-300 ${isMobile ? 'left-4' : ''}`}
                    style={!isMobile ? { left: sidebarOpen ? 'calc(18rem + 0.5rem)' : '0.5rem' } : undefined}
                >
                    <span className="block w-5 h-5 relative">
                        <span className={`absolute left-0 w-5 h-0.5 bg-current transition-all duration-300 ${sidebarOpen ? 'top-2 rotate-45' : 'top-1'}`} />
                        <span className={`absolute left-0 top-2 w-5 h-0.5 bg-current transition-all duration-300 ${sidebarOpen ? 'opacity-0' : 'opacity-100'}`} />
                        <span className={`absolute left-0 w-5 h-0.5 bg-current transition-all duration-300 ${sidebarOpen ? 'top-2 -rotate-45' : 'top-3'}`} />
                    </span>
                </button>

                {/* Main Content */}
                <main className="flex-1 overflow-y-auto">
                    <div className="max-w-4xl mx-auto p-8 pt-16 md:pt-8">
                        <GuideContent
                            guide={guide}
                            prevGuide={prevGuide}
                            nextGuide={nextGuide}
                            isGm={isGm}
                        />
                    </div>
                </main>
            </div>
        </>
    );
}

/**
 * GuideContent - Displays the guide content with markdown rendering.
 */
function GuideContent({ guide, prevGuide, nextGuide, isGm }: {
    guide: CurrentGuide;
    prevGuide: GuideNav | null;
    nextGuide: GuideNav | null;
    isGm: boolean;
}) {
    return (
        <article
            className="rounded-lg overflow-hidden"
            style={{
                background: 'linear-gradient(135deg, #f5edd6 0%, #e8dcc4 50%, #f0e6d0 100%)',
                boxShadow: '4px 4px 12px rgba(0,0,0,0.3), -1px -1px 4px rgba(0,0,0,0.1)',
                border: '1px solid #c9b896',
            }}
        >
            {/* Aged edge effect */}
            <div
                className="h-2 pointer-events-none"
                style={{ background: 'linear-gradient(180deg, rgba(139,115,85,0.15) 0%, transparent 100%)' }}
            />

            {/* Breadcrumb */}
            <div className="px-6 pt-4 flex items-center gap-2 text-sm text-amber-700/60">
                <Link href="/university" className="hover:text-amber-900 transition-colors">
                    🎓 University
                </Link>
                <span>→</span>
                <span className="text-amber-800">{guide.title}</span>
            </div>

            {/* Guide Header */}
            <header className="p-6 border-b border-amber-900/20">
                <div className="flex items-start gap-4">
                    <span className="text-5xl flex-shrink-0">{guide.icon}</span>
                    <div>
                        <h1 className="text-3xl font-serif font-bold text-amber-900">
                            {guide.title}
                        </h1>
                        {guide.description && (
                            <p className="mt-2 text-amber-800/70 text-lg">
                                {guide.description}
                            </p>
                        )}
                        {isGm && !guide.is_published && (
                            <span className="inline-block mt-2 px-2 py-0.5 bg-yellow-600 text-white text-xs rounded font-medium">
                                Draft
                            </span>
                        )}
                    </div>
                </div>
            </header>

            {/* Guide Content */}
            <div className="p-6 prose prose-amber max-w-none">
                <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                        h1: ({ children }) => (
                            <h1 className="text-2xl font-serif font-bold text-amber-900 border-b border-amber-900/20 pb-2 mb-4 mt-8 first:mt-0">
                                {children}
                            </h1>
                        ),
                        h2: ({ children }) => (
                            <h2 className="text-xl font-serif font-bold text-amber-900 mt-6 mb-3">
                                {children}
                            </h2>
                        ),
                        h3: ({ children }) => (
                            <h3 className="text-lg font-serif font-semibold text-amber-800 mt-4 mb-2">
                                {children}
                            </h3>
                        ),
                        p: ({ children }) => (
                            <p className="text-amber-900/90 leading-relaxed mb-4">
                                {children}
                            </p>
                        ),
                        a: ({ href, children }) => (
                            <a href={href} className="text-blue-700 hover:text-blue-900 underline">
                                {children}
                            </a>
                        ),
                        ul: ({ children }) => (
                            <ul className="list-disc list-inside text-amber-900/90 mb-4 space-y-1 ml-4">
                                {children}
                            </ul>
                        ),
                        ol: ({ children }) => (
                            <ol className="list-decimal list-inside text-amber-900/90 mb-4 space-y-1 ml-4">
                                {children}
                            </ol>
                        ),
                        li: ({ children }) => (
                            <li className="text-amber-900/90">{children}</li>
                        ),
                        blockquote: ({ children }) => (
                            <blockquote className="border-l-4 border-amber-600 pl-4 italic text-amber-800 my-4 bg-amber-900/5 py-2">
                                {children}
                            </blockquote>
                        ),
                        code: ({ children }) => (
                            <code className="bg-amber-900/10 px-1.5 py-0.5 rounded text-amber-900 text-sm font-mono">
                                {children}
                            </code>
                        ),
                        pre: ({ children }) => (
                            <pre className="bg-amber-900/10 p-4 rounded overflow-x-auto my-4 font-mono text-sm">
                                {children}
                            </pre>
                        ),
                        hr: () => <hr className="border-amber-900/20 my-6" />,
                        table: ({ children }) => (
                            <div className="overflow-x-auto my-4">
                                <table className="w-full border-collapse">
                                    {children}
                                </table>
                            </div>
                        ),
                        th: ({ children }) => (
                            <th className="border border-amber-900/30 bg-amber-900/10 px-3 py-2 text-left text-amber-900 font-serif font-semibold">
                                {children}
                            </th>
                        ),
                        td: ({ children }) => (
                            <td className="border border-amber-900/30 px-3 py-2 text-amber-900/90">
                                {children}
                            </td>
                        ),
                        strong: ({ children }) => (
                            <strong className="font-bold text-amber-900">{children}</strong>
                        ),
                        em: ({ children }) => (
                            <em className="italic text-amber-800">{children}</em>
                        ),
                    }}
                >
                    {guide.content}
                </ReactMarkdown>
            </div>

            {/* Navigation */}
            <footer className="p-6 border-t border-amber-900/20 flex justify-between bg-amber-900/5">
                {prevGuide ? (
                    <Link
                        href={`/university/${prevGuide.slug}`}
                        className="flex items-center gap-2 text-amber-700 hover:text-amber-900 transition-colors"
                    >
                        <span>←</span>
                        <span className="text-lg">{prevGuide.icon}</span>
                        <span>{prevGuide.title}</span>
                    </Link>
                ) : <div />}
                {nextGuide ? (
                    <Link
                        href={`/university/${nextGuide.slug}`}
                        className="flex items-center gap-2 text-amber-700 hover:text-amber-900 transition-colors"
                    >
                        <span>{nextGuide.title}</span>
                        <span className="text-lg">{nextGuide.icon}</span>
                        <span>→</span>
                    </Link>
                ) : <div />}
            </footer>
        </article>
    );
}
